// FEAT-B — on-screen touch controls for phone play. A floating left-thumb virtual joystick (feeds
// input.touchMove → camera-relative movement via the existing rotateIntent) plus small jump/slide buttons.
// Created only on touch devices; shown only during a run. The LEFT zone captures move touches so they don't
// also rotate the free camera (the right side / canvas keeps the yaw-drag from FEAT-C). Desktop is untouched.

import { Input } from '../core/input'
import { joystickVector } from '../core/joystick'

const JOY_RADIUS = 56

export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false
  // ?touch=1 forces the on-screen controls on (handy for desktop testing / preference)
  if (typeof location !== 'undefined' && /[?&]touch=1/.test(location.search)) return true
  return 'ontouchstart' in window || (typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches)
}

export class TouchControls {
  private wrap: HTMLDivElement
  private moveZone: HTMLDivElement
  private base: HTMLDivElement
  private knob: HTMLDivElement
  private movePointer: number | null = null
  private originX = 0
  private originY = 0

  constructor(root: HTMLElement, private input: Input) {
    this.wrap = document.createElement('div')
    this.wrap.id = 'touch-ui'
    this.wrap.innerHTML = `
      <div id="touch-move"><div class="joy-base"></div><div class="joy-knob"></div></div>
      <div id="touch-actions">
        <button id="touch-slide" class="touch-btn" aria-label="slide">»</button>
        <button id="touch-jump" class="touch-btn" aria-label="jump">▲</button>
      </div>`
    root.appendChild(this.wrap)
    this.moveZone = this.wrap.querySelector('#touch-move')!
    this.base = this.wrap.querySelector('.joy-base')!
    this.knob = this.wrap.querySelector('.joy-knob')!

    this.moveZone.addEventListener('pointerdown', this.onMoveDown)
    this.moveZone.addEventListener('pointermove', this.onMoveMove)
    this.moveZone.addEventListener('pointerup', this.onMoveUp)
    this.moveZone.addEventListener('pointercancel', this.onMoveUp)
    // safety nets: if a system gesture (iOS control-center swipe, incoming call, app switch) steals the touch
    // without delivering pointerup/cancel, clear the stick so the player doesn't keep walking with no finger down
    this.moveZone.addEventListener('lostpointercapture', this.onInterrupt)
    window.addEventListener('blur', this.onInterrupt)
    document.addEventListener('visibilitychange', this.onInterrupt)

    this.bindButton('#touch-jump', 'Space')
    this.bindButton('#touch-slide', 'ShiftLeft')
    this.setActive(false)
  }

  /** Show/hide the touch controls (only during a run). */
  setActive(on: boolean): void {
    this.wrap.style.display = on ? 'block' : 'none'
    if (!on) this.resetMove()
  }

  private resetMove(): void {
    this.movePointer = null
    this.input.touchMove = { x: 0, z: 0 }
    this.base.style.opacity = '0'
    this.knob.style.opacity = '0'
  }

  // released a captured/cancelled/backgrounded touch → drop the stick (only acts if the game is hidden or a
  // pointer is engaged; harmless otherwise)
  private onInterrupt = () => { if (this.movePointer !== null) this.resetMove() }

  private onMoveDown = (e: PointerEvent) => {
    if (this.movePointer !== null) return
    this.movePointer = e.pointerId
    this.originX = e.clientX
    this.originY = e.clientY
    try { this.moveZone.setPointerCapture(e.pointerId) } catch { /* ignore */ }
    this.placeJoystick(e.clientX, e.clientY, e.clientX, e.clientY)
    e.preventDefault()
  }
  private onMoveMove = (e: PointerEvent) => {
    if (e.pointerId !== this.movePointer) return
    const dx = e.clientX - this.originX
    const dy = e.clientY - this.originY
    this.input.touchMove = joystickVector(dx, dy, JOY_RADIUS)
    this.placeJoystick(this.originX, this.originY, e.clientX, e.clientY)
    e.preventDefault()
  }
  private onMoveUp = (e: PointerEvent) => {
    if (e.pointerId !== this.movePointer) return
    this.resetMove()
  }

  private placeJoystick(baseX: number, baseY: number, knobX: number, knobY: number): void {
    // clamp knob within the ring
    let dx = knobX - baseX
    let dy = knobY - baseY
    const m = Math.hypot(dx, dy)
    if (m > JOY_RADIUS) { dx = (dx / m) * JOY_RADIUS; dy = (dy / m) * JOY_RADIUS }
    this.base.style.left = `${baseX}px`
    this.base.style.top = `${baseY}px`
    this.knob.style.left = `${baseX + dx}px`
    this.knob.style.top = `${baseY + dy}px`
    this.base.style.opacity = '0.85'
    this.knob.style.opacity = '0.95'
  }

  // A press dispatches a synthetic key so it flows through the existing keyboard input path.
  private bindButton(sel: string, code: string): void {
    const el = this.wrap.querySelector<HTMLButtonElement>(sel)!
    const send = (type: 'keydown' | 'keyup') => window.dispatchEvent(new KeyboardEvent(type, { code, bubbles: true }))
    el.addEventListener('pointerdown', (e) => { e.preventDefault(); send('keydown') })
    el.addEventListener('pointerup', (e) => { e.preventDefault(); send('keyup') })
    el.addEventListener('pointercancel', () => send('keyup'))
    el.addEventListener('pointerleave', () => send('keyup'))
  }
}
